import { Check, CircleNotch } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export interface InlineEditOption {
  value: string;
  label: string;
}

interface InlineEditProps {
  /** Current value, always modelled as a string — callers coerce numbers/booleans. */
  value: string;
  /** Persist the new value. Throwing flips the control into an error state. */
  onSave: (value: string) => Promise<void>;
  type?: "text" | "number" | "select";
  /** Required when `type="select"`. */
  options?: InlineEditOption[];
  /**
   * Suggested values for a `type="text"` field, rendered via `<datalist>`.
   * Free text is still accepted — this only offers autocomplete.
   */
  listOptions?: string[];
  /** Display content while not editing. Defaults to the raw `value`. */
  children?: ReactNode;
  className?: string;
  inputClassName?: string;
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * Click-to-edit primitive shared by the command band and room cards. Renders
 * `children` (or `value`) as static text; a click swaps in an input/select
 * that commits on blur or Enter and reverts on Escape.
 */
export function InlineEdit({
  value,
  onSave,
  type = "text",
  options,
  listOptions,
  children,
  className,
  inputClassName,
  ariaLabel,
  placeholder,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const datalistId = useId();

  useEffect(() => {
    if (!editing) {
      return;
    }
    if (type === "select") {
      selectRef.current?.focus();
    } else {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing, type]);

  const startEdit = () => {
    setDraft(value);
    setStatus("idle");
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  const commit = async (nextValue: string) => {
    setEditing(false);
    if (nextValue === value) {
      return;
    }
    setStatus("saving");
    try {
      await onSave(nextValue);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
      window.setTimeout(() => setStatus("idle"), 2500);
    }
  };

  if (editing && type === "select") {
    return (
      <select
        ref={selectRef}
        aria-label={ariaLabel}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => void commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            cancel();
          }
        }}
        className={cn(
          "border-accent bg-card text-primary rounded border px-1.5 py-0.5 text-inherit outline-none",
          inputClassName,
        )}
      >
        {options?.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  if (editing) {
    return (
      <>
        <input
          ref={inputRef}
          aria-label={ariaLabel}
          type={type === "number" ? "number" : "text"}
          value={draft}
          placeholder={placeholder}
          list={listOptions && listOptions.length > 0 ? datalistId : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => void commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void commit((e.target as HTMLInputElement).value);
            } else if (e.key === "Escape") {
              cancel();
            }
          }}
          className={cn(
            "border-accent bg-card text-primary w-full min-w-0 rounded border px-1.5 py-0.5 text-inherit outline-none",
            inputClassName,
          )}
        />
        {listOptions && listOptions.length > 0 && (
          <datalist id={datalistId}>
            {listOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        )}
      </>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEdit();
        }
      }}
      title="Click to edit"
      className={cn(
        "hover:bg-accent/5 hover:ring-accent-light inline-flex cursor-text items-center gap-1.5 rounded transition-colors hover:ring-1",
        className,
      )}
    >
      {children ?? (value || <span className="text-faint">—</span>)}
      {status === "saving" && (
        <CircleNotch className="text-muted size-3 shrink-0 animate-spin" weight="light" />
      )}
      {status === "saved" && <Check className="text-success size-3 shrink-0" weight="light" />}
      {status === "error" && (
        <span className="text-destructive shrink-0 text-[10px] font-semibold">save failed</span>
      )}
    </span>
  );
}
